import CloseIcon from "@mui/icons-material/Close";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {
  Backdrop,
  Box,
  Button,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import TextField from '../../../components/SanitizedTextField/SanitizedTextField';
import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import AppLoader from "../../../components/AppLoader/AppLoader";
import CustomerInformationCard from "../../../components/CustomerInformationCard/CustomerInformationCard";
import FormSubmissionSuccessDialog from "../../../components/FormSubmissionSuccessDialog/FormSubmissionSuccessDialog";
import StepProgressBar from "../../../components/StepProgressBarForm/StepProgressBar";
import { cfpBaseUrl } from "../../../network/urls";
import {
  circleName,
  companyShortName,
  divisionName,
  handleFileUpload,
  subDivAPI,
  subdivisionName,
} from "../../../services/Functions";
import OpenBase64ImageInPopup from "./MrcOpenBase64ImageInPopup";
import { documentExtensions, portalName } from "../../../utils/PortalName";
import { resolveDiscoCompanyCodeForBranding } from "../../../utils/adminCompany";
import MrcEvidencePreview from "./MrcEvidencePreview";
import {
  buildCustomerIdYupRule,
  isValidCustomerIdLength,
  REFERENCE_NO_LENGTH,
} from "../../../utils/customerIdValidation";
import { printMrcApplicationReceipt } from "./printMrcApplicationReceipt";
import {
  sanitizeReadingInput,
  formatUnitsChargedDisplay,
  formatUnitsRevisedDisplay,
  getMrcEnteredReadingError,
  hasMrcPresentReading,
  // MRC_SEGREGATION_MONTH_DEFAULT,
  // normalizeMrcSegregationMonth,
} from "./mrcReadingUtils";
// import MrcSegregationMonthSelect from "./MrcSegregationMonthSelect";
import { formatCp52Date } from "./mrcCp52Api";
import "./MeterReadingCorrectionForm.scss";

/** Current API shape within a set: 1 = KWH, 2 = KVARH, 3 = MDI. */
const METER_ID_KWH = 1;
const METER_ID_KVARH = 2;
const METER_ID_MDI = 3;

/** Same mapping as Change of Attributes. */
const getConnectionTypeFromTariffCode = (tariffCode) => {
  const tariffMap = {
    "01": "Domestic",
    "02": "Commercial",
    "03": "Commercial",
    "04": "Industrial",
    "05": "Bulk Supply",
    "06": "Agricultural Tube Wells",
    "07": "Street Lighting",
    "08": "Residential Colonies",
  };
  const code = String(tariffCode ?? "").trim().padStart(2, "0");
  return tariffMap[code] || "General";
};

const normalizeTariffCode = (tariffCode) =>
  String(tariffCode ?? "").trim().padStart(2, "0");

const findTariffByCode = (tariffCode, tariffs = []) => {
  if (tariffCode == null || tariffCode === "") return null;
  const code = normalizeTariffCode(tariffCode);
  const raw = String(tariffCode).trim();
  return (
    tariffs.find((t) => {
      const apiCode = String(t.tariffCode ?? "").trim();
      return (
        apiCode.padStart(2, "0") === code ||
        apiCode === raw ||
        String(t.tariffName || "").includes(`(${code})`) ||
        String(t.tariffName || "").includes(`(${raw})`)
      );
    }) || null
  );
};

const getConnectionCategoryFromTariffList = (tariffCode, tariffs = []) => {
  const match = findTariffByCode(tariffCode, tariffs);
  if (!match) return "";
  return (
    match.consumerType ||
    match.connectionType ||
    match.category ||
    match.connectionCategory ||
    ""
  );
};

const resolveTariffNameFromList = (tariffCode, tariffs = []) => {
  const match = findTariffByCode(tariffCode, tariffs);
  return match?.tariffName || "";
};

/** Show tariff name with code in Applicant Details, e.g. A1a(01). */
const formatTariffDisplay = (tariffName, tariffCode) => {
  const code = normalizeTariffCode(tariffCode);
  const raw = String(tariffCode ?? "").trim();
  const name = String(tariffName || "").trim();
  if (!code && !name) return "";
  if (!name) return code || raw;
  if (
    (code && name.includes(`(${code})`)) ||
    (raw && name.includes(`(${raw})`))
  ) {
    return name;
  }
  return `${name}(${code || raw})`;
};

const formatBillAmount = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value).trim();
  return n.toLocaleString("en-PK", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const createEmptyMeterRows = () => [
  {
    id: "kwh",
    setNo: "1",
    meterType: "KWH",
    serialNo: "",
    mf: "",
    previousReading: "",
    asChargedReading: "",
    claimedReading: "",
    unitsCharged: "",
    claimedError: "",
    claimedImage: "",
    claimedImageThumbnail: "",
    claimedImageError: "",
    meterCode: "",
    cmoNo: "",
    meterId: "",
    tsNo: "",
    feederCode: "",
    meterRange: "",
    mdiResetNo: "",
  },
  {
    id: "kvarh",
    setNo: "2",
    meterType: "KVARH",
    serialNo: "",
    mf: "",
    previousReading: "",
    asChargedReading: "",
    claimedReading: "",
    unitsCharged: "",
    claimedError: "",
    claimedImage: "",
    claimedImageThumbnail: "",
    claimedImageError: "",
    meterCode: "",
    cmoNo: "",
    meterId: "",
    tsNo: "",
    feederCode: "",
    meterRange: "",
    mdiResetNo: "",
  },
  {
    id: "mdi",
    setNo: "3",
    meterType: "MDI",
    serialNo: "",
    mf: "",
    previousReading: "",
    asChargedReading: "",
    claimedReading: "",
    unitsCharged: "",
    claimedError: "",
    claimedImage: "",
    claimedImageThumbnail: "",
    claimedImageError: "",
    meterCode: "",
    cmoNo: "",
    meterId: "",
    tsNo: "",
    feederCode: "",
    meterRange: "",
    mdiResetNo: "",
  },
];

const toNumber = (value) => {
  const n = Number(String(value ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const formatReadingValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = toNumber(value);
  return n == null ? String(value).trim() : String(n);
};

/** Units Adj. = (Claimed Reading − As Charged) × MF — decimals to 2 digits */
const calcUnitsAdjusted = (claimedReading, asChargedReading, mf) => {
  // Only when claimed is entered (empty string must not become 0 via toNumber)
  if (claimedReading === "" || claimedReading == null) return "";
  const claimed = toNumber(claimedReading);
  const asCharged = toNumber(asChargedReading);
  const factor = toNumber(mf) ?? 1;
  if (claimed == null || asCharged == null) return "";
  const adjusted = (claimed - asCharged) * (factor === 0 ? 1 : factor);
  return Number.isFinite(adjusted) ? formatUnitsRevisedDisplay(adjusted, "") : "";
};

/** Units Chg. = round((As Charged − Previous) × MF) */
const calcUnitsCharged = (asChargedReading, previousReading, mf) => {
  const asCharged = toNumber(asChargedReading);
  const previous = toNumber(previousReading);
  if (asCharged == null || previous == null) return "";
  const factor = toNumber(mf) ?? 1;
  const charged = (asCharged - previous) * (factor === 0 ? 1 : factor);
  return Number.isFinite(charged) ? String(Math.round(charged)) : "";
};

/** Units Revised = (Claimed Reading − Previous) × MF */
const calcUnitsChargedRevised = (claimedReading, previousReading, mf) => {
  if (claimedReading === "" || claimedReading == null) return "";
  const claimed = toNumber(claimedReading);
  const previous = toNumber(previousReading);
  if (claimed == null || previous == null) return "";
  const factor = toNumber(mf) ?? 1;
  const revised = (claimed - previous) * (factor === 0 ? 1 : factor);
  return Number.isFinite(revised) ? formatUnitsRevisedDisplay(revised, "") : "";
};

const formatDisplayValue = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

const normalizeMeterCode = (code) => String(code ?? "").trim();

const readingFromConnectionRow = (row, isMdiType) => {
  if (!row) return "";
  if (isMdiType) {
    const mdi = formatReadingValue(row.MDI_CUM_READ);
    if (mdi !== "") return mdi;
  }
  return formatReadingValue(row.METER_READING);
};

const previousReadingFromRow = (row) => {
  if (!row) return "";
  const prv = formatReadingValue(row.PRV_READING);
  if (prv !== "") return prv;
  return "";
};

/** Meter ID: 1 = KWH, 2 = KVARH, 3 = MDI. Off Peak / Peak only for KWH (ID 1). */
const NET_METER_KWH_LABEL_BY_SET = {
  1: "Import Off-Peak KWH",
  2: "Import Peak KWH",
  3: "Export Off-Peak KWH",
  4: "Export Peak KWH",
};

const netMeterDirectionFromSet = (setNo) => {
  const n = Number(setNo);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n >= 3 ? "Export" : "Import";
};

const joinMeterLabel = (...parts) =>
  parts.filter((part) => part != null && String(part).trim() !== "").join(" ");

const peakOffPeakFromMeterCode = (code) => {
  const c = String(code ?? "").trim().toLowerCase();
  if (!c) return null;
  if (
    c === "050600" ||
    c.endsWith("0600") ||
    c.endsWith("0506") ||
    (c.includes("peak") && !c.includes("off"))
  ) {
    return "Peak";
  }
  if (
    c === "050500" ||
    c.endsWith("0500") ||
    c.endsWith("0505") ||
    c.includes("off")
  ) {
    return "Off Peak";
  }
  return null;
};

const meterLabelFromConnectionRow = (
  row,
  { isNetMeter = false, isTou = false } = {}
) => {
  const meterId = Number(row?.METER_ID);
  const setNo = Number(toNumber(row.SET_NO) ?? row.SET_NO);
  const code = normalizeMeterCode(row?.METER_CODE);
  const direction = isNetMeter ? netMeterDirectionFromSet(setNo) : "";
  const band = peakOffPeakFromMeterCode(code);

  if (meterId === METER_ID_MDI) {
    return joinMeterLabel(direction, band, "MDI") || "MDI";
  }

  if (meterId === METER_ID_KVARH) {
    if (band) return joinMeterLabel(direction, band, "KVARH");
    if ((isNetMeter || isTou) && Number.isFinite(setNo) && setNo > 0) {
      const setBand = setNo % 2 === 1 ? "Off Peak" : "Peak";
      return joinMeterLabel(direction, setBand, "KVARH");
    }
    return joinMeterLabel(direction, "KVARH") || "KVARH";
  }

  if (isNetMeter && NET_METER_KWH_LABEL_BY_SET[setNo]) {
    return NET_METER_KWH_LABEL_BY_SET[setNo];
  }
  if (band) return joinMeterLabel(direction, "KWH", band);
  if ((isNetMeter || isTou) && Number.isFinite(setNo) && setNo > 0) {
    const setBand = setNo % 2 === 1 ? "Off Peak" : "Peak";
    return joinMeterLabel(direction, "KWH", setBand);
  }
  return joinMeterLabel(direction, "KWH") || "KWH";
};

const isMdiMeterRow = (row) =>
  String(row?.meterType || "").toUpperCase() === "MDI" ||
  Number(row?.meterId) === METER_ID_MDI;

const hasClaimedReadingEntered = (row) => {
  const v = row?.claimedReading;
  return v !== "" && v != null && String(v).trim() !== "";
};

const hasClaimedImageEntered = (row) =>
  Boolean(row?.claimedImageThumbnail || row?.claimedImage);

/** Row is being corrected if claimed reading or evidence image is present. */
const isMeterCorrectionStarted = (row) =>
  !isMdiMeterRow(row) &&
  (hasClaimedReadingEntered(row) || hasClaimedImageEntered(row));


/** Set 1 → base, Set 2 → base2, Set 3 → base3, Set 4 → base4 */
const billedUnitsKeyForSet = (baseKey, setNo) => {
  const n = Number(setNo);
  if (n >= 2 && n <= 4) return `${baseKey}${n}`;
  return baseKey;
};

const pickBilledUnits = (source, key) => {
  if (!source || !key) return null;
  const raw = source[key];
  if (raw === null || raw === undefined || raw === "") return null;
  return toNumber(raw);
};

const pickBilledUnitsForSet = (baseKey, setNo, ...sources) => {
  const key = billedUnitsKeyForSet(baseKey, setNo);
  for (const source of sources) {
    const value = pickBilledUnits(source, key);
    if (value != null) return value;
  }
  return null;
};

/** Prefer API billed units when present; else (As Charged − Previous) × MF. */
const unitsChargedFromConnectionRow = (
  row,
  asChargedReading,
  previousReading,
  mf,
  consumer
) => {
  const meterId = Number(row?.METER_ID);
  const setNo = toNumber(row?.SET_NO) ?? Number(row?.SET_NO);
  const fromApi =
    meterId === METER_ID_KWH
      ? pickBilledUnitsForSet("CUR_KWH_CHR", setNo, consumer, row)
      : meterId === METER_ID_KVARH
        ? pickBilledUnitsForSet("TOT_KVARH_CHR", setNo, consumer, row)
        : meterId === METER_ID_MDI
          ? pickBilledUnitsForSet("CUR_MDI_CHR", setNo, consumer, row)
          : null;
  if (fromApi != null) {
    return String(Number(fromApi.toFixed(2)));
  }
  return calcUnitsCharged(asChargedReading, previousReading, mf);
};

const readingsFromConnectionObject = (row, consumer) => {
  const meterId = Number(row?.METER_ID);
  const previousReading = previousReadingFromRow(row);
  const asChargedReading =
    meterId === METER_ID_MDI
      ? readingFromConnectionRow(row, true)
      : formatReadingValue(row.METER_READING);
  const mf = formatReadingValue(row.MULTIPLY_FACTOR);
  const unitsCharged = unitsChargedFromConnectionRow(
    row,
    asChargedReading,
    previousReading,
    mf,
    consumer
  );

  return { previousReading, asChargedReading, unitsCharged };
};

/** One table row per connectionInfo object (e.g. 12 objects → 12 rows). */
const buildMeterRowsFromConnectionInfo = (
  connectionInfo = [],
  consumer = null
) => {
  if (!Array.isArray(connectionInfo) || connectionInfo.length === 0) {
    return createEmptyMeterRows();
  }

  const kwhSets = new Set();
  connectionInfo.forEach((row) => {
    if (Number(row?.METER_ID) !== METER_ID_KWH) return;
    const setNo = toNumber(row.SET_NO) ?? Number(row.SET_NO);
    if (Number.isFinite(setNo) && setNo > 0) kwhSets.add(setNo);
  });
  const phase = String(
    consumer?.meterPhaseType || consumer?.METER_PHASE_TYPE || ""
  ).toLowerCase();
  const isNetMeter =
    phase.includes("net") ||
    kwhSets.has(3) ||
    kwhSets.has(4) ||
    kwhSets.size >= 4;
  // 2+ KWH sets (non-net) → TOU Off Peak / Peak labels
  const isTou = !isNetMeter && kwhSets.size >= 2;

  return [...connectionInfo]
    .sort((a, b) => {
      const setDiff =
        (toNumber(a.SET_NO) ?? 0) - (toNumber(b.SET_NO) ?? 0);
      if (setDiff !== 0) return setDiff;
      return (toNumber(a.METER_ID) ?? 0) - (toNumber(b.METER_ID) ?? 0);
    })
    .map((row, index) => {
      const setNo = String(toNumber(row.SET_NO) ?? row.SET_NO ?? "").trim();
      const meterCode = normalizeMeterCode(row.METER_CODE);
      const meterType = meterLabelFromConnectionRow(row, { isNetMeter, isTou });
      const { previousReading, asChargedReading, unitsCharged } =
        readingsFromConnectionObject(row, consumer);
      const meterId = formatDisplayValue(row.METER_ID);

      return {
        id: `set-${setNo}-meter-${meterId || index}-${meterCode || meterType}`,
        setNo,
        meterType,
        serialNo: formatDisplayValue(row.METER_SERIAL_NO).trim(),
        mf: formatReadingValue(row.MULTIPLY_FACTOR),
        previousReading,
        asChargedReading,
        claimedReading: "",
        unitsCharged,
        claimedError: "",
        claimedImage: "",
        claimedImageThumbnail: "",
        claimedImageError: "",
        meterCode,
        cmoNo: formatDisplayValue(row.CMO_NO),
        meterId,
        tsNo: formatDisplayValue(row.TS_NO),
        feederCode: formatDisplayValue(row.FEEDER_CODE),
        meterRange: formatDisplayValue(row.METER_RANGE),
        mdiResetNo: formatDisplayValue(row.MDI_RESET_NO ?? 0),
      };
    });
};

const resolveTopLevelFeederCode = (rows = [], values = {}) => {
  const fromForm = String(values.iv_feeder_code ?? "").trim();
  if (fromForm) return fromForm;
  for (const row of rows) {
    const code = String(row.feederCode ?? "").trim();
    if (code) return code;
  }
  return "";
};

const initialValues = {
  iv_search_by: "consumerId",
  iv_reference_number: "",
  iv_consumer_id: "",
  iv_applicant_name: "",
  iv_father_name: "",
  iv_applicant_cnic: "",
  iv_applicant_phone_no: "",
  iv_connection_type: "",
  iv_tariff_description: "",
  iv_circle: "",
  iv_division: "",
  iv_sub_divison: "",
  iv_address: "",
  iv_billing_month: "",
  // iv_segregation_month: String(MRC_SEGREGATION_MONTH_DEFAULT),
  iv_due_date: "",
  iv_reading_date: "",
  iv_wapda_employee: "",
  iv_tariff_code: "",
  iv_telco: "",
  iv_contact_no: "",
  iv_lps: "",
  iv_lps_5p: "",
  iv_total_amount: "",
  iv_cc_code: "",
  iv_sanctioned_load: "",
  iv_feeder_code: "",
  iv_transformer_ref_no: "",
};

const MeterReadingCorrectionForm = () => {
  const navigate = useNavigate();
  const { token, code, roleId, selectedAdminCompanyCode, csc, userId } =
    useSelector((state) => state.auth);
  const companyCode = resolveDiscoCompanyCodeForBranding(
    code,
    roleId,
    selectedAdminCompanyCode
  );
  const userCompany = String(companyCode ?? "").slice(0, 2);
  const isAdminCompanyUser = String(code) === "10";
  const subDivisionsArray = JSON.parse(
    window.localStorage.getItem("subDivisions")
  );

  const [viewBackdrop, setViewBackdrop] = useState(false);
  /** API fetch/submit — same popup style as success (ID + copy) */
  const [apiErrorDialog, setApiErrorDialog] = useState({
    open: false,
    message: "",
    trackingId: "",
  });
  /** Meter / form validation only — top Snackbar (not inside table) */
  const [validationError, setValidationError] = useState("");
  const [consumerFetched, setConsumerFetched] = useState(false);
  const [meterRows, setMeterRows] = useState(createEmptyMeterRows);
  const [successOpen, setSuccessOpen] = useState(false);
  const [trackingId, setTrackingId] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const lastRequestedIdRef = useRef("");

  const closeApiErrorDialog = () =>
    setApiErrorDialog({ open: false, message: "", trackingId: "" });

  const showApiErrorDialog = (message, id = "") => {
    const text =
      typeof message === "string" && message.trim()
        ? message.trim()
        : "Request failed. Please try again.";
    const tracking =
      id != null && String(id).trim() !== "" ? String(id).trim() : "";
    setApiErrorDialog({
      open: true,
      message: text,
      trackingId: tracking,
    });
  };

  const pickApiErrorId = (result) => {
    // Error popup: only show ID if API actually returned one (never invent on failed submit).
    const raw =
      result?.eOpsTrackingId ||
      result?.trackingId ||
      result?.applicationId ||
      result?.applicationID ||
      "";
    return String(raw).trim();
  };

  const [circleList, setCircleList] = useState([]);
  const [divisionList, setDivisionList] = useState([]);
  const [subDivisionList, setSubDivisionList] = useState([]);
  const [defaultCircle, setDefaultCircle] = useState();
  const [defaultDivision, setDefaultDivision] = useState();
  const [subDivision, setSubDivision] = useState({ code: null, name: "" });
  const [consumerCompanyName, setConsumerCompanyName] = useState("");
  const [tarifData, setTarifData] = useState([]);

  const stepBar = useMemo(
    () => [
      {
        title1: "APPLICATION",
        title2: "DETAIL",
        status: 0,
      },
    ],
    []
  );

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      iv_search_by: Yup.string().required(),
      iv_reference_number: Yup.string().when("iv_search_by", {
        is: "referenceNo",
        then: Yup.string()
          .required("Enter reference number.")
          .length(
            REFERENCE_NO_LENGTH,
            `Reference No. must be ${REFERENCE_NO_LENGTH} digits.`
          ),
        otherwise: Yup.string().optional(),
      }),
      iv_consumer_id: Yup.string().when("iv_search_by", {
        is: "consumerId",
        then: buildCustomerIdYupRule(code),
        otherwise: Yup.string().optional(),
      }),
      iv_applicant_name: Yup.string(),
      iv_father_name: Yup.string(),
      iv_applicant_cnic: Yup.string(),
      iv_applicant_phone_no: Yup.string()
        .required("Please enter phone number.")
        .matches(/^03[0-9]{9}$/, "Enter Phone number as 03001234567"),
      iv_telco: Yup.string()
        .required("Please select a telco.")
        .test(
          "telco-selected",
          "Please select a telco.",
          (value) => ["1", "2", "3", "4"].includes(String(value ?? "").trim())
        ),
      iv_contact_no: Yup.string()
        .nullable()
        .notRequired()
        .test(
          "mobile-format",
          "Enter Phone number as 03001234567",
          (value) => !value || /^03[0-9]{9}$/.test(value)
        ),
      // iv_segregation_month: Yup.string()
      //   .required("Select no. of segregate months.")
      //   .oneOf(["1", "2", "3"], "No. of segregate months must be 1, 2, or 3."),
    }),
    onSubmit: async () => {
      // Submit handled in handleFinalSubmit after validation.
    },
  });

  useEffect(() => {
    const loadHierarchy = async () => {
      if (!subDivisionsArray) return;
      const lists = await subDivAPI(subDivisionsArray);
      setCircleList(lists.circleList || []);
      setDivisionList(lists.divisionList || []);
      setSubDivisionList(lists.subDivisionList || []);
    };
    loadHierarchy();
    tariffListAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tariffListAPI = async () => {
    if (!token) return [];
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      const response = await fetch(
        cfpBaseUrl + "customerFaciliationPortal/getTariffsDetail",
        {
          method: "POST",
          body: new FormData(),
          headers: myHeaders,
          redirect: "follow",
        }
      );
      const result = await response.json();
      if (String(result?.status) === "200" && Array.isArray(result?.data)) {
        setTarifData(result.data);
        return result.data;
      }
    } catch (e) {
      /* ignored */
    }
    return [];
  };

  const resolveCategoryAndTariffName = async (tariffCode) => {
    let tariffs = tarifData;
    if (!tariffs?.length) {
      tariffs = (await tariffListAPI()) || [];
    }
    const category =
      getConnectionCategoryFromTariffList(tariffCode, tariffs) ||
      getConnectionTypeFromTariffCode(tariffCode);
    const tariffName =
      resolveTariffNameFromList(tariffCode, tariffs) || tariffCode;
    return { category, tariffName };
  };

  const clearFetchedConsumer = () => {
    lastRequestedIdRef.current = "";
    setConsumerFetched(false);
    setMeterRows(createEmptyMeterRows());
    formik.setFieldValue("iv_applicant_name", "");
    formik.setFieldValue("iv_father_name", "");
    formik.setFieldValue("iv_applicant_cnic", "");
    formik.setFieldValue("iv_applicant_phone_no", "");
    formik.setFieldValue("iv_contact_no", "");
    formik.setFieldValue("iv_telco", "");
    formik.setFieldValue("iv_connection_type", "");
    formik.setFieldValue("iv_tariff_description", "");
    formik.setFieldValue("iv_address", "");
    formik.setFieldValue("iv_billing_month", "");
    // formik.setFieldValue(
    //   "iv_segregation_month",
    //   String(MRC_SEGREGATION_MONTH_DEFAULT)
    // );
    formik.setFieldValue("iv_due_date", "");
    formik.setFieldValue("iv_reading_date", "");
    formik.setFieldValue("iv_wapda_employee", "");
    formik.setFieldValue("iv_tariff_code", "");
    formik.setFieldValue("iv_lps", "");
    formik.setFieldValue("iv_lps_5p", "");
    formik.setFieldValue("iv_total_amount", "");
    formik.setFieldValue("iv_cc_code", "");
    formik.setFieldValue("iv_sanctioned_load", "");
    formik.setFieldValue("iv_feeder_code", "");
    formik.setFieldValue("iv_transformer_ref_no", "");
    formik.setFieldValue("iv_circle", "");
    formik.setFieldValue("iv_division", "");
    formik.setFieldValue("iv_sub_divison", "");
    setDefaultCircle();
    setDefaultDivision();
    setSubDivision({ code: null, name: "" });
    setConsumerCompanyName("");
  };

  const SearchBy = (event) => {
    formik.handleChange(event);
    formik.setFieldValue("iv_consumer_id", "");
    formik.setFieldValue("iv_reference_number", "");
    clearFetchedConsumer();
    closeApiErrorDialog();
    setValidationError("");
  };

  const resolveOfficeCompanyCode = (refNo, sdivCode) => {
    const sdiv = String(sdivCode || "").trim();
    if (sdiv.length >= 2) return sdiv.slice(0, 2);
    const ref = String(refNo || "").replace(/\D/g, "");
    if (ref.length >= 4) return ref.slice(2, 4);
    return String(companyCode || code || "").slice(0, 2);
  };

  const fetchCompanyHierarchy = async (compCode) => {
    if (!compCode || !token) return [];
    try {
      const formdata = new FormData();
      formdata.append("companyCode", String(compCode));
      const response = await fetch(
        cfpBaseUrl + "customerFaciliationPortal/subDivWithCompDetails",
        {
          method: "POST",
          headers: { Authorization: token },
          body: formdata,
          redirect: "follow",
        }
      );
      const result = await response.json();
      if (
        String(result?.status) === "200" &&
        Array.isArray(result?.message)
      ) {
        return result.message;
      }
    } catch (e) {
      /* ignore */
    }
    return [];
  };

  const applyHierarchyFromReference = async (refNo, sdivCode, divCode) => {
    const reference = String(refNo || "").replace(/\D/g, "").slice(0, 14);
    let hierarchy = Array.isArray(subDivisionsArray) ? subDivisionsArray : [];
    if (!reference && !sdivCode && !divCode) return;

    const subDivisionCode = sdivCode
      ? String(sdivCode).trim()
      : reference
        ? reference.slice(2, 7)
        : "";

    // Loose match (same as subdivisionName helper).
    let match = subDivisionCode
      ? hierarchy.find((item) => item.subDivisionCode == subDivisionCode)
      : null;

    // Local storage may not have this DISCO's offices (e.g. Super Admin).
    if (!match) {
      const officeCompany = resolveOfficeCompanyCode(refNo, sdivCode);
      const remoteHierarchy = await fetchCompanyHierarchy(officeCompany);
      if (remoteHierarchy.length) {
        hierarchy = remoteHierarchy;
        match = hierarchy.find((item) => item.subDivisionCode == subDivisionCode);
        const lists = await subDivAPI(remoteHierarchy);
        setCircleList(lists.circleList || []);
        setDivisionList(lists.divisionList || []);
        setSubDivisionList(lists.subDivisionList || []);
      }
    }

    const circleCode = match
      ? match.circleCode
      : reference
        ? reference.slice(2, 5)
        : sdivCode
          ? String(sdivCode).trim().slice(0, 3)
          : "";

    const divisionCode = match
      ? match.divisionCode
      : divCode
        ? String(divCode).trim()
        : reference
          ? reference.slice(2, 6)
          : sdivCode
            ? String(sdivCode).trim().slice(0, 4)
            : "";

    const divisions = hierarchy
      .filter((item) => item.circleCode == circleCode)
      .reduce((unique, item) => {
        if (!unique.find((d) => d.code == item.divisionCode)) {
          unique.push({
            code: item.divisionCode,
            name: item.divisionName,
          });
        }
        return unique;
      }, [])
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "en", {
          sensitivity: "base",
        })
      );
    if (divisions.length) setDivisionList(divisions);

    const subDivisions = hierarchy
      .filter((item) => item.divisionCode == divisionCode)
      .reduce((unique, item) => {
        if (!unique.find((s) => s.code == item.subDivisionCode)) {
          unique.push({
            code: item.subDivisionCode,
            name: item.subDivisionName,
          });
        }
        return unique;
      }, [])
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""), "en", {
          sensitivity: "base",
        })
      );
    if (subDivisions.length) setSubDivisionList(subDivisions);

    const resolvedCircleName =
      (match && match.circleName) ||
      circleName(circleCode, hierarchy) ||
      circleList.find((item) => item.code == circleCode)?.name ||
      String(circleCode || "");

    const resolvedDivisionName =
      (match && match.divisionName) ||
      divisionName(divisionCode, hierarchy) ||
      divisions.find((item) => item.code == divisionCode)?.name ||
      String(divisionCode || "");

    const resolvedSubDivisionName =
      (match && match.subDivisionName) ||
      subdivisionName(subDivisionCode, hierarchy) ||
      subDivisions.find((item) => item.code == subDivisionCode)?.name ||
      String(subDivisionCode || "");

    if (circleCode !== "" && circleCode != null) {
      const circleObject = {
        code: match?.circleCode ?? circleCode,
        name: resolvedCircleName,
      };
      setDefaultCircle(circleObject);
      formik.setFieldValue("iv_circle", circleObject.code);
    }

    if (divisionCode !== "" && divisionCode != null) {
      const divisionObject = {
        code: match?.divisionCode ?? divisionCode,
        name: resolvedDivisionName,
      };
      setDefaultDivision(divisionObject);
      formik.setFieldValue("iv_division", divisionObject.code);
    }

    if (subDivisionCode !== "" && subDivisionCode != null) {
      const subDivisionObject = {
        code: match?.subDivisionCode ?? subDivisionCode,
        name: resolvedSubDivisionName,
      };
      setSubDivision(subDivisionObject);
      formik.setFieldValue("iv_sub_divison", subDivisionObject.code);
    }

    if (isAdminCompanyUser) {
      const officeCompany = resolveOfficeCompanyCode(refNo, sdivCode);
      setConsumerCompanyName(companyShortName(String(officeCompany)));
      } else {
      setConsumerCompanyName("");
    }
  };

  const resolvedCompanyCode = String(companyCode || code || "").slice(0, 2);

  const searchBy = formik.values.iv_search_by;
  const canFetchConsumer =
    searchBy === "referenceNo"
      ? String(formik.values.iv_reference_number || "").trim().length ===
        REFERENCE_NO_LENGTH
      : isValidCustomerIdLength(
          formik.values.iv_consumer_id,
          resolvedCompanyCode
        );

  const fetchConsumerDetails = async (searchValue, searchType = "consumerId") => {
    const lookupValue = String(searchValue || "").trim();
    if (!lookupValue) return;
    const requestKey = `${searchType}:${lookupValue}`;
    if (lastRequestedIdRef.current === requestKey) return;

    lastRequestedIdRef.current = requestKey;
    setViewBackdrop(true);
    closeApiErrorDialog();
    setValidationError("");

    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const payload =
      {
        searchById: lookupValue,
        // referenceNo: "",
        companyCode: resolvedCompanyCode,
        // companyCode: "13",
      };
          

      const response = await fetch(cfpBaseUrl + "mrc/getMRCConsumerDetails", {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(payload),
        redirect: "follow",
      });

      const result = await response.json();

      if (
        String(result?.status) === "200" &&
        Array.isArray(result?.consumerInfo) &&
        result.consumerInfo.length > 0
      ) {
        const consumer = result.consumerInfo[0];
        const refNo = String(consumer.CKEY || "")
          .replace(/\D/g, "")
          .slice(0, 14);
        const appNo = String(consumer.APPLICATION_NO || "").trim();
        const address = [consumer.ADDR_1, consumer.ADDR_2]
          .filter(Boolean)
          .join(" ")
          .trim();
        const cnic = String(
          consumer.NEW_NIC_NO ?? consumer.OCCUPANT_CNIC_NO ?? ""
        ).replace(/\D/g, "");
        const phone = consumer.CONTACT_NO
          ? String(consumer.CONTACT_NO).replace(/\D/g, "").replace(/^92/, "0")
          : "";
        const billing = result?.billingInfo?.[0] || {};
        const billingMonth = String(billing.B_PERIOD || "").trim();
        const dueDate = formatCp52Date(billing.DUE_DATE || billing.dueDate || "");
        const readingDate = formatCp52Date(
          billing.READING_DATE ?? billing.readingDate ?? ""
        );
        // Total Amount = billing NET_FOR_BILL (total bill amount)
        const totalAmountRaw =
          billing.NET_FOR_BILL ??
          billing.net_for_bill ??
          billing.CUR_NET ??
          "";
        const totalAmount = formatBillAmount(totalAmountRaw);
        const lpsAmount = formatBillAmount(
          billing.LPSUR_CHRG ?? billing.lpsur_chrg ?? ""
        );
        const lps5PAmount = formatBillAmount(
          billing.LPSUR_CHRG5P ?? billing.lpsur_chrg5p ?? ""
        );
        const tariffCode = String(consumer.TARIFF_ACTIVE || "").trim();
        const { category, tariffName } =
          await resolveCategoryAndTariffName(tariffCode);
        const weFlag = String(consumer.WE_FLAG ?? "")
          .trim()
          .toUpperCase();
        const isWapdaEmployee = weFlag === "Y";
        const wapdaEmployee = isWapdaEmployee ? "YES" : "NO";

        // Keep both IDs marked so auto-fill of the other field does not re-fetch.
        lastRequestedIdRef.current = [
          `consumerId:${appNo || lookupValue}`,
          refNo ? `referenceNo:${refNo}` : "",
          requestKey,
        ]
          .filter(Boolean)
          .join("|");

        formik.setFieldValue("iv_reference_number", refNo);
        formik.setFieldValue("iv_consumer_id", appNo);
        formik.setFieldValue("iv_applicant_name", consumer.CONSUMER_NAME || "");
        formik.setFieldValue("iv_father_name", consumer.FNAME || "");
        formik.setFieldValue("iv_applicant_cnic", cnic);
        formik.setFieldValue("iv_applicant_phone_no", phone);
        formik.setFieldValue("iv_contact_no", phone);
        formik.setFieldValue("iv_telco", "");
        formik.setFieldValue("iv_address", address);
        formik.setFieldValue("iv_tariff_code", tariffCode);
        formik.setFieldValue(
          "iv_tariff_description",
          formatTariffDisplay(tariffName, tariffCode)
        );
        formik.setFieldValue("iv_connection_type", category);
        formik.setFieldValue("iv_wapda_employee", wapdaEmployee);
        formik.setFieldValue("iv_billing_month", billingMonth);
        // formik.setFieldValue(
        //   "iv_segregation_month",
        //   String(MRC_SEGREGATION_MONTH_DEFAULT)
        // );
        formik.setFieldValue("iv_due_date", dueDate);
        formik.setFieldValue("iv_reading_date", readingDate);
        formik.setFieldValue("iv_lps", lpsAmount);
        formik.setFieldValue("iv_lps_5p", lps5PAmount);
        formik.setFieldValue("iv_total_amount", totalAmount);
        formik.setFieldValue(
          "iv_cc_code",
          String(consumer.CC_CODE ?? "").trim()
        );
        formik.setFieldValue(
          "iv_sanctioned_load",
          formatReadingValue(consumer.SAN_LOAD)
        );
        const firstFeederCode =
          (result.connectionInfo || [])
            .map((row) => String(row.FEEDER_CODE ?? "").trim())
            .find(Boolean) || "";
        formik.setFieldValue("iv_feeder_code", firstFeederCode);
        formik.setFieldValue(
          "iv_transformer_ref_no",
          String(
            consumer.TRANSFORMER_REF_NO ??
              consumer.TRANSFORMER_REFNO ??
              consumer.TRANSFORMER_REF ??
              consumer.transformerRefNo ??
              result.transformerRefNo ??
              ""
          ).trim()
        );

        await applyHierarchyFromReference(
          refNo,
          consumer.SDIV,
          consumer.DIVCODE
        );

        if (isWapdaEmployee) {
          setMeterRows(createEmptyMeterRows());
          setConsumerFetched(false);
          showApiErrorDialog(
            "This connection belongs to a WAPDA Employee. Meter Reading Correction cannot be applied on WAPDA Employee connections. Please contact your concerned subdivision office for further assistance."
          );
          return;
        }

        setMeterRows(
          buildMeterRowsFromConnectionInfo(
            result.connectionInfo || [],
            consumer
          )
        );
        setConsumerFetched(true);
        closeApiErrorDialog();
        setValidationError("");
      } else {
        lastRequestedIdRef.current = "";
        clearFetchedConsumer();
        showApiErrorDialog(
          result?.message ||
            result?.innerMessage ||
            "No Data Found against this search criteria.",
          pickApiErrorId(result)
        );
      }
    } catch (e) {
      lastRequestedIdRef.current = "";
      clearFetchedConsumer();
      showApiErrorDialog("An error occurred while fetching consumer details.");
    } finally {
      setViewBackdrop(false);
    }
  };

  // Auto-fetch when Customer ID or Reference No. length is complete.
  useEffect(() => {
    const activeSearchBy = formik.values.iv_search_by;
    const consumerId = String(formik.values.iv_consumer_id || "").trim();
    const referenceNo = String(formik.values.iv_reference_number || "").trim();
    const alreadyRequested = (type, value) =>
      String(lastRequestedIdRef.current).includes(`${type}:${value}`);

    if (activeSearchBy === "referenceNo") {
      if (referenceNo.length === REFERENCE_NO_LENGTH) {
        if (alreadyRequested("referenceNo", referenceNo)) return;
        fetchConsumerDetails(referenceNo, "referenceNo");
      } else if (consumerFetched) {
        clearFetchedConsumer();
      }
      return;
    }

    if (isValidCustomerIdLength(consumerId, resolvedCompanyCode)) {
      if (alreadyRequested("consumerId", consumerId)) return;
      fetchConsumerDetails(consumerId, "consumerId");
    } else if (consumerFetched) {
      clearFetchedConsumer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.iv_consumer_id,
    formik.values.iv_reference_number,
    formik.values.iv_search_by,
    resolvedCompanyCode,
  ]);

  const handleCardChange = (event) => {
    const name = event?.target?.name;
    const value = event?.target?.value;
    if (name === "iv_consumer_id" || name === "iv_reference_number") {
      lastRequestedIdRef.current = "";
      formik.setFieldValue(name, value);
      if (consumerFetched) clearFetchedConsumer();
      return;
    }
    if (name === "iv_applicant_phone_no") {
      const digits = String(value || "")
        .replace(/\D/g, "")
        .slice(0, 11);
      formik.setFieldValue("iv_applicant_phone_no", digits);
      formik.setFieldValue("iv_contact_no", digits);
      return;
    }
    if (name === "iv_telco") {
      formik.setFieldValue("iv_telco", String(value ?? ""));
      return;
    }
    formik.handleChange(event);
  };

  const handleCardBlur = (event) => {
    formik.handleBlur(event);
  };

  const handleClaimedReadingChange = (rowId, value) => {
    const cleaned = sanitizeReadingInput(value);
    if (value !== "" && cleaned === "" && value.replace(/[^\d.]/g, "") === "") {
      return;
    }

    setMeterRows((prev) => {
      const next = prev.map((row) => {
        if (row.id !== rowId) return row;

        const nextRow = { ...row, claimedReading: cleaned };
        return {
          ...nextRow,
          claimedError: getClaimedReadingError(nextRow),
          claimedImageError: getClaimedImageError(nextRow),
        };
      });

      const firstMsg =
        next.map((r) => r.claimedError).find(Boolean) ||
        next.map((r) => r.claimedImageError).find(Boolean) ||
        "";
      queueMicrotask(() => setValidationError(firstMsg));

      return next;
    });
  };

  const handleClaimedImageUpload = (rowId, event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    const newFileName = `claimedReading-set-${rowId}.${file.name
      .split(".")
      .pop()}`;

    const setFile = (val) =>
      setMeterRows((prev) =>
        prev.map((row) =>
          row.id === rowId ? { ...row, claimedImage: val } : row
        )
      );
    const setErr = (err) => {
      const message = err || "";
      setMeterRows((prev) =>
        prev.map((row) =>
          row.id === rowId ? { ...row, claimedImageError: message } : row
        )
      );
      if (message) setValidationError(message);
    };
    const setThumb = (thumb) =>
      setMeterRows((prev) => {
        const next = prev.map((row) => {
          if (row.id !== rowId) return row;
          const nextRow = {
            ...row,
            claimedImageThumbnail: thumb || "",
          };
          return {
            ...nextRow,
            claimedError: getClaimedReadingError(nextRow),
            claimedImageError: getClaimedImageError(nextRow),
          };
        });
        const firstMsg =
          next.map((r) => r.claimedError).find(Boolean) ||
          next.map((r) => r.claimedImageError).find(Boolean) ||
          "";
        queueMicrotask(() => setValidationError(firstMsg));
        return next;
      });

    handleFileUpload(
      event,
      () => {},
      setFile,
      setErr,
      setThumb,
      newFileName
    );
  };

  const clearClaimedImage = (rowId) => {
    setMeterRows((prev) => {
      const next = prev.map((row) => {
        if (row.id !== rowId) return row;
        const nextRow = {
          ...row,
          claimedImage: "",
          claimedImageThumbnail: "",
        };
        return {
          ...nextRow,
          claimedError: getClaimedReadingError(nextRow),
          claimedImageError: getClaimedImageError(nextRow),
        };
      });
      const firstMsg =
        next.map((r) => r.claimedError).find(Boolean) ||
        next.map((r) => r.claimedImageError).find(Boolean) ||
        "";
      queueMicrotask(() => setValidationError(firstMsg));
      return next;
    });
  };

  const getClaimedReadingError = (row) => {
    if (isMdiMeterRow(row)) return "";

    const claimedEntered = hasClaimedReadingEntered(row);
    const imageEntered = hasClaimedImageEntered(row);

    // Evidence uploaded → claimed reading is required
    if (imageEntered && !claimedEntered) {
      return "Claimed Reading is required when image/PDF is uploaded.";
    }

    // Optional unless user starts correcting this row
    if (!claimedEntered) return "";

    const claimed = toNumber(row.claimedReading);
    if (claimed == null) {
      return "Claimed Reading must be numeric.";
    }
    return getMrcEnteredReadingError(
      claimed,
      {
        previous: row.previousReading,
        asCharged: row.asChargedReading,
        mf: row.mf,
        unitsCharged: row.unitsCharged,
      },
      "Claimed Reading"
    );
  };

  const getClaimedImageError = (row) => {
    if (isMdiMeterRow(row)) return "";

    const claimedEntered = hasClaimedReadingEntered(row);
    const imageEntered = hasClaimedImageEntered(row);

    // Claimed reading entered → evidence (image/PDF) is required
    if (claimedEntered && !imageEntered) {
      return "Image or PDF is required when Claimed Reading is entered.";
    }
    return "";
  };

  const validateMeterRows = () => {
    const nextRows = meterRows.map((row) => ({
      ...row,
      claimedError: getClaimedReadingError(row),
      claimedImageError: getClaimedImageError(row),
    }));
    setMeterRows(nextRows);

    const claimedMsgs = [
      ...new Set(
        nextRows
          .map((row) => row.claimedError)
          .filter(Boolean)
      ),
    ];
    const imageMsgs = [
      ...new Set(
        nextRows
          .map((row) => row.claimedImageError)
          .filter(Boolean)
      ),
    ];

    const rowsOk = nextRows.every(
      (row) => !row.claimedError && !row.claimedImageError
    );
    if (!rowsOk) {
      setValidationError(
        claimedMsgs[0] ||
          imageMsgs[0] ||
          "Please correct meter reading errors."
      );
      return false;
    }

    const hasAnyChange = nextRows.some((row) => isMeterCorrectionStarted(row));
    if (!hasAnyChange) {
      setValidationError(
        "Please enter Claimed Reading and image for at least one meter you want to correct."
      );
      return false;
    }

    setValidationError("");
    return true;
  };

  // Meter validation only — API errors use success-style popup.
  const snackbarError = validationError || "";

  const validateApplicationStep = async () => {
    const errors = await formik.validateForm();
    const fields = [
      searchBy === "referenceNo" ? "iv_reference_number" : "iv_consumer_id",
      "iv_telco",
      "iv_applicant_phone_no",
    ];
    const touchMap = {};
    fields.forEach((field) => {
      touchMap[field] = true;
    });
    formik.setTouched({ ...formik.touched, ...touchMap }, true);

    const formFieldsOk =
      !fields.some((field) => errors[field]) && canFetchConsumer;

    if (!consumerFetched) {
      setValidationError("Please fetch consumer details before continuing.");
      return false;
    }

    if (!validateMeterRows()) {
      return false;
    }

    return formFieldsOk;
  };

  const handleFinalSubmit = async () => {
    closeApiErrorDialog();
    setValidationError("");
    if (!(await validateApplicationStep())) return;

    setViewBackdrop(true);
    try {
      const values = formik.values;
      const payableWithinDueDate = toNumber(values.iv_total_amount) ?? 0;
      const lps = toNumber(values.iv_lps) ?? 0;
      const lps5P = toNumber(values.iv_lps_5p) ?? 0;

      const meterData = meterRows.map((row) => ({
        meterId: toNumber(row.meterId) ?? 0,
        setNo: toNumber(row.setNo) ?? 0,
        tsNo: toNumber(row.tsNo) ?? 0,
        cmoNo: toNumber(row.cmoNo) ?? 0,
        feederCode: String(row.feederCode ?? "").trim(),
        meterSerialNo: String(row.serialNo ?? "").trim(),
        meterCode: String(row.meterCode ?? "").trim(),
        meterRange: toNumber(row.meterRange) ?? 0,
        mdiResetNo: toNumber(row.mdiResetNo) ?? 0,
        previousReadCharged: toNumber(row.previousReading) ?? 0,
        presentReadCharged: toNumber(row.asChargedReading) ?? 0,
        presentUnitsCharged: toNumber(row.unitsCharged) ?? 0,
        consumerClaimedReading: toNumber(row.claimedReading) ?? 0,
        consumerClaimedImage: row.claimedImageThumbnail || "",
        mf: toNumber(row.mf) ?? 1,
      }));

      const payload = {
        submittedThrough: portalName,
        cfcId: String(csc ?? ""),
        referenceNo: String(values.iv_reference_number || "").trim(),
        customerId: String(values.iv_consumer_id || "").trim(),
        telcoCode: String(values.iv_telco || "").trim(),
        applicantMobile: String(values.iv_applicant_phone_no || "").trim(),
        consumerName: String(values.iv_applicant_name || "").trim(),
        consumerAddress: String(values.iv_address || "").trim(),
        consumerCNIC: String(values.iv_applicant_cnic || "").trim(),
        tariffCode: String(values.iv_tariff_code || "").trim(),
        ccCode: String(values.iv_cc_code || "").trim(),
        billingMonth: String(values.iv_billing_month || "").trim(),
        // noOfMonths: normalizeMrcSegregationMonth(values.iv_segregation_month),
        weFlag: String(values.iv_wapda_employee || "").toUpperCase() === "YES",
        dueDate: String(values.iv_due_date || "").trim(),
        readingDate: String(values.iv_reading_date || "").trim(),
        payableWithinDueDate,
        lps,
        lps5P,
        sanctionedLoad: toNumber(values.iv_sanctioned_load) ?? 0,
        feederCode: resolveTopLevelFeederCode(meterRows, values),
        transformerRefNo: String(values.iv_transformer_ref_no ?? "").trim(),
        createdBy: String(userId ?? ""),
        meterData,
      };

      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");

      const response = await fetch(cfpBaseUrl + "mrc/registerMRCApplication", {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(payload),
        redirect: "follow",
      });
      const result = await response.json();

      if (String(result?.status) === "200") {
        const id =
          result.eOpsTrackingId ||
          result.trackingId ||
          result.applicationId ||
          "";
        const referenceNo =
          result.referenceNo ||
          values.iv_reference_number ||
          values.iv_consumer_id ||
          "";
        if (id) {
          await printMrcApplicationReceipt({
            values,
            applicationID: id,
            issuedBy: userId,
            referenceNo,
            readingRows: meterRows,
            // Branding / watermark from application subdivision (consumer DISCO)
            companyCode: String(
              values.iv_sub_divison || values.iv_circle || companyCode || ""
            ).slice(0, 2),
            userCompany: String(
              values.iv_sub_divison || values.iv_circle || userCompany || ""
            ).slice(0, 2),
            subDivisions: subDivisionsArray,
          });
        }
        setTrackingId(String(id));
        setSuccessOpen(true);
        formik.resetForm({ values: initialValues });
        clearFetchedConsumer();
        setMeterRows(createEmptyMeterRows());
        closeApiErrorDialog();
        setValidationError("");
      } else {
        const errMsg =
          typeof result?.message === "string"
            ? result.message
            : Array.isArray(result?.message) && result.message.length > 0
              ? result.message[0]
              : result?.innerMessage ||
                "Failed to submit request! Please try later.";
        showApiErrorDialog(errMsg, pickApiErrorId(result));
      }
    } catch (e) {
      showApiErrorDialog("Failed to submit request! Please try later.");
    } finally {
      setViewBackdrop(false);
    }
  };

  return (
    <>
      <Grid
        container
        className="mrc-page"
        sx={{ width: "100%", maxWidth: "100%" }}
      >
        <Box
          className="mrc-container"
          sx={{ width: "100%", maxWidth: "100%" }}
        >
          <h3 className="heading mrc-page-title">Meter Reading Correction</h3>
          <div className="mrc-stepper-wrap">
            <StepProgressBar array={stepBar} pageName="PP Form" />
          </div>

          <form
            className="mrc-form"
            onSubmit={(e) => e.preventDefault()}
            noValidate
          >
              <>
                <div className="section-card section-card--accent">
                  <div className="section-title">Applicant&apos;s Details</div>
            <CustomerInformationCard
                    searchBy={formik.values.iv_search_by}
              SearchBy={SearchBy}
                    allowedSearchBy={["referenceNo", "consumerId"]}
                    errors={formik.errors}
                    values={formik.values}
                    touched={formik.touched}
                    handleChange={handleCardChange}
                    handleBlur={handleCardBlur}
              defaultCircle={defaultCircle}
              circleList={circleList}
              setDefaultCircle={setDefaultCircle}
              defaultDivision={defaultDivision}
              divisionList={divisionList}
              setDefaultDivision={setDefaultDivision}
              subDivision={subDivision}
              subDivisionList={subDivisionList}
              setSubDivision={setSubDivision}
                    layoutVariant="alb"
                    showCompany={isAdminCompanyUser}
                    companyDisplayName={consumerCompanyName}
                    showTelco
                    hidePhase
                    hideMeterType
                  />
                </div>

                <div className="section-card section-card--bill">
                  <div className="section-title">Bill Details</div>
                  <Grid
                    container
                    spacing={1.5}
                    className="container mrc-bill-details-grid"
                    alignItems="flex-end"
                  >
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap">
                        <Typography className="fieldLabel">
                          Billing Month
              </Typography>
                    <TextField
                          fullWidth
                      disabled
                          name="iv_billing_month"
                          value={formik.values.iv_billing_month}
                          className="textInputField1"
                          placeholder="DD-MMM-YY"
                        />
                  </div>
                </Grid>
                    {/* <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <MrcSegregationMonthSelect
                        name="iv_segregation_month"
                        value={formik.values.iv_segregation_month}
                        onChange={formik.handleChange}
                      />
                    </Grid> */}
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap">
                        <Typography className="fieldLabel">Due Date</Typography>
                    <TextField
                          fullWidth
                          disabled
                          name="iv_due_date"
                          value={formik.values.iv_due_date}
                          className="textInputField1"
                          placeholder="DD-MMM-YY"
                        />
                  </div>
                </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap">
                        <Typography className="fieldLabel">
                          Reading Date
                        </Typography>
                    <TextField
                          fullWidth
                          disabled
                          name="iv_reading_date"
                          value={formik.values.iv_reading_date}
                          className="textInputField1"
                          placeholder="DD-MMM-YY"
                        />
                  </div>
                </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap">
                        <Typography className="fieldLabel">
                          Wapda Employee
                        </Typography>
                    <TextField
                          fullWidth
                      disabled
                          name="iv_wapda_employee"
                          value={formik.values.iv_wapda_employee}
                          className="textInputField1"
                          placeholder="YES / NO"
                        />
                  </div>
                </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap">
                        <Typography className="fieldLabel">LPS</Typography>
                    <TextField
                          fullWidth
                      disabled
                          name="iv_lps"
                          value={formik.values.iv_lps}
                          className="textInputField1"
                        />
                  </div>
                </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap">
                        <Typography className="fieldLabel">LPS 5P</Typography>
                    <TextField
                          fullWidth
                          disabled
                          name="iv_lps_5p"
                          value={formik.values.iv_lps_5p}
                          className="textInputField1"
                        />
                  </div>
                </Grid>
                    <Grid item xs={12} sm={6} md={4} lg={4} xl={2}>
                      <div className="field-wrap field-wrap--total-amount">
                        <Typography className="fieldLabel">
                          Total Amount
                        </Typography>
                    <TextField
                          fullWidth
                      disabled
                          name="iv_total_amount"
                          value={formik.values.iv_total_amount}
                          className="textInputField1"
                          title="Total Amount (NET_FOR_BILL)"
                        />
                  </div>
                </Grid>
                </Grid>
                  </div>

                {consumerFetched ? (
                  <div className="section-card section-card--meter">
                    <div className="section-title">Meter Information</div>
                    <p className="mrc-meter-hint">
                      Enter <strong>User Claimed Reading</strong> and upload{" "}
                      <strong>image or PDF</strong> only for meters you want to
                      correct. Both are required together. Units Chg. uses
                      billed units from consumer data when available; otherwise
                      (As Charged − Previous) × MF.
                    </p>
                    <div className="mrc-meter-table">
                      <table>
                        <colgroup>
                          <col className="col-set" />
                          <col className="col-meter" />
                          <col className="col-serial" />
                          <col className="col-mf" />
                          <col className="col-num" />
                          <col className="col-num" />
                          <col className="col-num" />
                          <col className="col-claimed" />
                          <col className="col-num" />
                          <col className="col-revised" />
                          <col className="col-image" />
                        </colgroup>
                      <thead>
                        <tr>
                            <th>Set</th>
                            <th>Meter</th>
                            <th>Serial No.</th>
                            <th>MF</th>
                            <th>Previous</th>
                            <th>As Charged</th>
                            <th>Units Chg.</th>
                            <th>Claimed Reading</th>
                            <th>Units Adj.</th>
                            <th>Units Rev.</th>
                            <th>Evidence</th>
                        </tr>
                      </thead>
                      <tbody>
                          {meterRows.map((row) => {
                            const isMdi = isMdiMeterRow(row);
                            const correctionStarted = isMeterCorrectionStarted(row);
                            const claimedError = correctionStarted
                              ? row.claimedError
                              : "";
                            const imageError = correctionStarted
                              ? row.claimedImageError
                              : "";
                            const unitsAdjusted =
                              isMdi || !hasMrcPresentReading(row.claimedReading)
                              ? ""
                              : calcUnitsAdjusted(
                                  row.claimedReading,
                                  row.asChargedReading,
                                  row.mf
                                );
                            const unitsChargedRevised =
                              isMdi || !hasMrcPresentReading(row.claimedReading)
                              ? ""
                              : calcUnitsChargedRevised(
                                  row.claimedReading,
                                  row.previousReading,
                                  row.mf
                                );

                            return (
                              <tr key={row.id}>
                                <td className="cell-text cell-set">
                                  {formatDisplayValue(row.setNo) || "—"}
                            </td>
                                <td className="cell-text cell-meter">
                                  <span
                                    className="meter-type-pill"
                                    title={formatDisplayValue(row.meterType).replace(
                                      /\n/g,
                                      " "
                                    )}
                                  >
                                    {formatDisplayValue(row.meterType) || "—"}
                            </span>
                            </td>
                                <td className="cell-text cell-serial">
                                  {formatDisplayValue(row.serialNo) || "—"}
                            </td>
                                <td className="cell-text cell-mf">
                                  {formatDisplayValue(row.mf) || "—"}
                                </td>
                                <td className="cell-text cell-num">
                                  {formatDisplayValue(row.previousReading) ||
                                    "—"}
                                </td>
                                <td className="cell-text cell-num">
                                  {formatDisplayValue(row.asChargedReading) ||
                                    "—"}
                                </td>
                                <td className="cell-text cell-num cell-calc">
                                  {formatUnitsChargedDisplay(row.unitsCharged)}
                                </td>
                                <td
                                  className={
                                    isMdi
                                      ? "cell-text cell-num"
                                      : "cell-claimed"
                                  }
                                >
                                  {isMdi ? (
                                    "—"
                                  ) : (
                                    <div className="mrc-meter-claimed-wrap">
                      <TextField
                                        fullWidth
                                        size="small"
                                        value={row.claimedReading}
                                        onChange={(e) =>
                                          handleClaimedReadingChange(
                                            row.id,
                                            e.target.value
                                          )
                                        }
                                        placeholder="Enter reading"
                                        className="textInputField mrc-meter-input mrc-meter-input--editable"
                                        error={Boolean(claimedError)}
                                        title={claimedError || undefined}
                                        inputProps={{
                                          inputMode: "decimal",
                                          step: "0.01",
                                          "aria-label": `Claimed reading set ${row.setNo}`,
                                          title: claimedError || undefined,
                                        }}
                                      />
                        </div>
                                  )}
                            </td>
                                <td className="cell-text cell-num cell-calc">
                                  {unitsAdjusted !== ""
                                    ? unitsAdjusted
                                    : "—"}
                            </td>
                                <td className="cell-text cell-num cell-calc">
                                  {unitsChargedRevised !== ""
                                    ? unitsChargedRevised
                                    : "—"}
                                </td>
                                <td className="cell-image">
                                  {isMdi ? (
                                    <div className="mrc-meter-image-wrap">
                                      <span className="cell-text">—</span>
                  </div>
                                  ) : (
                                  <div className="mrc-meter-image-wrap">
                                    {row.claimedImage ? (
                                      <div
                                        className="mrc-meter-image-preview mrc-meter-image-preview--clickable"
                                        role="button"
                                        tabIndex={0}
                                        title="Click to view"
                          onClick={() => {
                                          const src =
                                            row.claimedImageThumbnail ||
                                            (typeof row.claimedImage === "string"
                                              ? row.claimedImage
                                              : "");
                                          if (!src) return;
                                          setPreviewImage({
                                            src,
                                            name: `${row.meterType || "Meter"} — Set ${row.setNo} Evidence`,
                                          });
                                        }}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            const src =
                                              row.claimedImageThumbnail ||
                                              (typeof row.claimedImage ===
                                              "string"
                                                ? row.claimedImage
                                                : "");
                                            if (!src) return;
                                            setPreviewImage({
                                              src,
                                              name: `${row.meterType || "Meter"} — Set ${row.setNo} Evidence`,
                                            });
                                          }
                                        }}
                                      >
                                        <MrcEvidencePreview
                                          src={
                                            row.claimedImageThumbnail ||
                                            (typeof row.claimedImage === "string"
                                              ? row.claimedImage
                                              : "")
                                          }
                                          alt={`Claimed reading set ${row.setNo}`}
                                        />
                                        <button
                                          type="button"
                                          className="mrc-meter-image-preview__clear"
                                          aria-label={`Remove evidence set ${row.setNo}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            clearClaimedImage(row.id);
                                          }}
                                        >
                                          <CloseIcon fontSize="inherit" />
                                        </button>
                        </div>
                      ) : (
                    <Button
                      variant="outlined"
                      component="label"
                                        size="small"
                                        startIcon={
                                          <FileUploadOutlinedIcon fontSize="small" />
                                        }
                                        className={`mrc-meter-image-btn${
                                          imageError
                                            ? " mrc-meter-image-btn--error"
                                            : ""
                                        }`}
                                        title="Upload JPG, PNG, or PDF"
                                      >
                                        Upload
                      <input
                        hidden
                        accept={documentExtensions}
                        type="file"
                                          onChange={(e) =>
                                            handleClaimedImageUpload(
                                              row.id,
                                              e
                                            )
                                          }
                      />
                    </Button>
                                    )}
                          </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}
              </>

            <div className="submit-area">
              <Button
                variant="contained"
                className="submit-btn"
                onClick={handleFinalSubmit}
                disabled={!token}
              >
                Submit
              </Button>
            </div>
          </form>
            </Box>
      </Grid>

      <Snackbar
        open={Boolean(snackbarError)}
        autoHideDuration={7000}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setValidationError("");
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 72, sm: 88 } }}
      >
        <div
          className="mrc-validation-toast"
          role="alert"
          aria-live="assertive"
        >
          <div className="mrc-validation-toast__icon-wrap" aria-hidden="true">
            <WarningRoundedIcon className="mrc-validation-toast__icon" />
          </div>
          <span className="mrc-validation-toast__divider" aria-hidden="true" />
          <div className="mrc-validation-toast__body">
            <p className="mrc-validation-toast__eyebrow">Error</p>
            <p className="mrc-validation-toast__message">{snackbarError}</p>
          </div>
          <IconButton
            type="button"
            size="small"
            className="mrc-validation-toast__close"
            onClick={() => setValidationError("")}
            aria-label="Dismiss validation message"
            sx={{ position: "absolute", top: 6, right: 6 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </Snackbar>

      <FormSubmissionSuccessDialog
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          setTrackingId("");
          navigate("../meter-reading-correction-table");
        }}
        title="Request submitted"
        message="Your Meter Reading Correction complaint is submitted successfully."
        trackingId={trackingId}
      />

      <FormSubmissionSuccessDialog
        open={apiErrorDialog.open}
        onClose={closeApiErrorDialog}
        variant="error"
        eyebrow="Request failed"
        title="Unable to complete request"
        message={apiErrorDialog.message}
        trackingId={apiErrorDialog.trackingId}
        trackingLabel="Reference / ID"
      />

      {previewImage?.src ? (
        <OpenBase64ImageInPopup
          docVerifyPopUp={Boolean(previewImage)}
          hideDocVerifyPopup={() => setPreviewImage(null)}
          documentName={previewImage.name || "Evidence"}
          documentImage={previewImage.src}
          width="100%"
          document={previewImage.src}
          readOnly
        />
      ) : null}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={viewBackdrop}
      >
        <AppLoader label="Please wait..." />
      </Backdrop>
    </>
  );
};

export default MeterReadingCorrectionForm;
